import { isEmpty, isEqual, isNull, keys, zipObject } from 'lodash';
import React, { useState } from 'react';
import { normalizeRichTextContent, validate } from '../helpers';

export type InputErrorState = Record<string, string>;
export type InputDirtyState = Record<string, boolean>;
export type InputOptionalState = Record<string, boolean>;
export type InputValidatorParamsState = Record<string, any>;

/**
 * Hook for managing form field values, validation, dirty state, and optional fields.
 *
 * @template T - Shape of the form fields (record of field names to initial values).
 * @param fields - Initial field values keyed by field name.
 * @param validator - Function to validate a single field. Receives field name, current values,
 *   optional field flags (for cases like radio toggles), and optional extra params for the validator.
 *   Returns an error string (empty if valid). Defaults to `validate`.
 * @param validateOnBlur - Whether to run validation on blur. Defaults to true.
 * @param validateOnChange - Whether to run validation on change.
 * @param validatorFields - Extra key-value data passed to the validator when it runs.
 * @param options - Optional config.
 * @param options.excludeFields - Field names excluded from value equality (e.g. File objects).
 *   Those fields are not compared with `isEqual`, but if `dirty[field]` is true they still make `inputIsDirty()` true.
 * @param options.richTextFields - Field names whose values are HTML and need normalization.
 * @returns Object with values, errors, dirty state, handlers (onChange, onBlur, etc.), and helpers.
 */
export const useInputFields = <T extends Record<string, any>>(
  fields: T,
  validator: (
    name: string,
    values: Record<string, any>,
    optional?: Record<string, boolean>,
    validatorParams?: Record<string, T>,
  ) => string = validate,
  validateOnBlur = true,
  validateOnChange?: boolean,
  validatorFields = {},
  options?: {
    excludeFields?: string[];
    richTextFields?: string[];
  },
) => {
  // Initilize.
  const errorFields: InputErrorState = {};
  Object.keys(fields).forEach((field) => {
    errorFields[field] = '';
  });

  const dirtyFields: InputDirtyState = {};

  const optionalFields: InputOptionalState = {};
  Object.keys(fields).forEach((field) => {
    dirtyFields[field] = false;
  });

  Object.keys(fields).forEach((field) => {
    optionalFields[field] = false;
  });

  const validatorParamFields = {};
  Object.assign(validatorParamFields, validatorFields);

  // States.
  const [values, setValues] = useState<T>(fields);
  const [originalValues, setOriginalValues] = useState<T | null>(null);
  const [errors, setErrors] = useState(errorFields);
  const [dirty, setDirty] = useState(dirtyFields);
  const [optional, setOptional] = useState(optionalFields);
  const [validatorParams, setValidatorParams] = useState(validatorParamFields);
  const names: Record<keyof T, keyof T> = zipObject(
    keys(fields),
    keys(fields),
  ) as Record<keyof T, keyof T>;

  // Handlers.
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value[0] !== ' ') {
      let newValue: string | null = event.target.value;

      if (event.target.type == 'number' && isEmpty(newValue)) {
        newValue = null;
      }

      const newValues = { ...values, [event.target.name]: newValue };

      if (!isNull(event.target.name) && !dirty[event.target.name]) {
        setDirty({
          ...dirty,
          [event.target.name]: true,
        });
      }

      if (validateOnChange) {
        setErrors({
          ...errors,
          [event.target.name]: validator(
            event.target.name,
            newValues,
            optional,
            validatorParams,
          ),
        });
      }
      setValues(newValues);
    }
  };

  const onBlur = (
    event: React.FocusEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLDivElement
    >,
  ) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const targetName = target?.name;

    if (isEmpty(targetName)) {
      return;
    }

    if (validateOnBlur) {
      setErrors({
        ...errors,
        [targetName]: validator(targetName, values, optional, validatorParams),
      });
    }
  };

  const onFocus = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setErrors({
      ...errors,
      [event.target.name]: '',
    });
  };

  // Error won't be checked for optional fields.
  const inputHasError = (optionalFields?: string[]): boolean => {
    const tempErrors: InputErrorState = {};
    let errorFlag = false;
    for (const valueKey of Object.keys(values)) {
      if (!optionalFields?.includes(valueKey)) {
        const error = validator(valueKey, values, optional, validatorParams);
        tempErrors[valueKey] = error;
        if (!isEmpty(error)) {
          errorFlag = true;
        }
      }
    }
    return errorFlag;
  };

  const inputIsDirty = () => {
    // If we have original values and options for enhanced comparison, use actual value comparison.
    if (originalValues && options) {
      // Remove excluded fields from comparison.
      const excludeFields = options.excludeFields || [];
      const currentEntries = Object.entries(values).filter(
        ([key]) => !excludeFields.includes(key),
      );
      const originalEntries = Object.entries(originalValues).filter(
        ([key]) => !excludeFields.includes(key),
      );

      // Normalize rich text fields for comparison.
      const richTextFields = options.richTextFields || [];
      const currentForComparison = Object.fromEntries(
        currentEntries.map(([key, value]) => [
          key,
          richTextFields.includes(key)
            ? normalizeRichTextContent(value)
            : value,
        ]),
      );
      const originalForComparison = Object.fromEntries(
        originalEntries.map(([key, value]) => [
          key,
          richTextFields.includes(key)
            ? normalizeRichTextContent(value)
            : value,
        ]),
      );

      // Compare the cleaned objects; excluded keys rely on `dirty` (e.g. file pickers set dirty without comparable values).
      const includedFieldDirty = !isEqual(
        currentForComparison,
        originalForComparison,
      );
      const excludedFieldDirty = excludeFields.some((key) => dirty[key]);
      return includedFieldDirty || excludedFieldDirty;
    }

    let dirtyFlag = false;
    for (const dirtyValue of Object.values(dirty)) {
      if (dirtyValue) {
        dirtyFlag = true;
        break;
      }
    }
    return dirtyFlag;
  };

  const resetAll = () => {
    setDirty(dirtyFields);
    setValues(fields);
    setErrors(errorFields);
    setOptional(optionalFields);
    setOriginalValues(null);
  };

  const validateAllFields = () => {
    const tempErrors: InputErrorState = {};
    for (const valueKey of Object.keys(values)) {
      tempErrors[valueKey] = validator(
        valueKey,
        values,
        optional,
        validatorParams,
      );
    }
    setErrors(() => tempErrors);
    return isEmpty(tempErrors);
  };

  const getOptionalFields = () => {
    return Object.keys(optional).filter((key) => {
      return optional[key];
    });
  };

  return {
    names,
    values,
    setValues,
    originalValues,
    setOriginalValues,
    errors,
    setErrors,
    dirty,
    setDirty,
    optional,
    setOptional,
    onChange,
    onBlur,
    onFocus,
    inputHasError,
    inputIsDirty,
    resetAll,
    validateAllFields,
    validatorParams,
    setValidatorParams,
    getOptionalFields,
  };
};
